<template>
    <!--
        The navbar strip: `h-(--ui-header-height)` with a bottom hairline, the page TITLE at the
        left and the controls at the right. The title is the current section's menu label
        (`navTitleKey`), so it is the same word the sidebar shows and no new copy is introduced.

        It paints `--ui-bg-chrome` — the same surface the sidebar takes, so THE FRAME IS ONE
        SURFACE: white over the neutral-100 canvas in light, and in dark the panel colour, the
        same mix the cards take, over the darker neutral-900 canvas. The hairline below it is
        the seam, not the separation.

        The user menu is NOT here any more — it lives at the bottom of the sidebar, where an
        admin console puts it. What stays is what belongs to the whole window rather than to the
        person: the light/dark toggle (vue-spec `theme.md`).
    -->
    <header class="h-(--ui-header-height) shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 border-b border-default bg-(--ui-bg-chrome)">
        <div class="flex items-center gap-2 min-w-0">
            <!-- Opens the drawer. It does not have to close it: once open, the drawer covers
                 this corner, and the X inside it and the backdrop are the ways out. -->
            <UButton
                v-if="isMobile"
                color="neutral"
                variant="ghost"
                icon="i-lucide-menu"
                :aria-label="$t('layout.open_menu')"
                @click="openMenu"
            />
            <h1 class="font-semibold text-highlighted truncate">{{ $t(titleKey) }}</h1>
        </div>

        <div class="flex items-center shrink-0 gap-1">
            <!-- FLEET: the console's language, beside the colour mode — both belong to the
                 window rather than to the person, and this console is read from a phone in a
                 yard as much as from a desk, where the topbar is the only surface. -->
            <USelect
                v-model="selectedLocale"
                :items="localeOptions"
                value-key="value"
                size="sm"
                variant="ghost"
                class="w-32"
                :aria-label="$t('locale.label')"
            />

            <ColorModeToggle />
        </div>
    </header>
</template>

<script lang="ts" setup>
    import { computed, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRoute } from 'vue-router'

    import ColorModeToggle from '@/layouts/components/ColorModeToggle.vue'
    import { navTitleKey, useLayout } from '@/layouts/layout'
    import { useAppStore } from '@/stores/app'
    import type { SelectOption } from '@/types/Common'

    const route = useRoute()
    const { isMobile, openMenu } = useLayout()

    const titleKey = computed((): string => navTitleKey(String(route.name ?? '')))

    // FLEET: the language select (see the template above).
    const { t }    = useI18n()
    const appStore = useAppStore()

    const localeOptions = computed((): SelectOption[] => [
        { label: t('locale.en'), value: 'en' },
        { label: t('locale.sl'), value: 'sl' },
        { label: t('locale.hr'), value: 'hr' },
    ])

    const selectedLocale = ref(appStore.locale)

    watch(selectedLocale, (value) => appStore.changeLocale(value))
</script>
